import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'react-bootstrap';
import { SIZE } from 'baseui/button';
import { FormControl as FormControlBaseWeb } from 'baseui/form-control';

import store from 'store';
import { paths } from 'frameworks_drivers/routes';
import { TrackerUtil } from 'tracking/TrackerUtil';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import { Button } from '@techem/ui-components';
import option1Icon from 'presentation/assets/icons/svg/icn-fernwaerme.svg';
import option2Icon from 'presentation/assets/icons/svg/icn-gas.svg';
import option3Icon from 'presentation/assets/icons/svg/icn-heizoel.svg';
import option4Icon from 'presentation/assets/icons/svg/icn-pellets.svg';
import option5Icon from 'presentation/assets/icons/svg/icn-solar.svg';
import option6Icon from 'presentation/assets/icons/svg/icn-waermepumpe.svg';
import option7Icon from 'presentation/assets/icons/svg/icn-sonstiges.svg';
import { setHeatingSources as setHeatingSourcesInStore } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import {
  CheckboxOptionKeys,
  sortSelectedOptions,
  translationKeyForCheckboxOption
} from 'tools/CheckboxOptionMapper';
import PageDescription from 'presentation/components/PageDescription';
import Icon, { getPixelSize, ICONSIZE } from 'presentation/components/Icon';
import Checkbox from 'presentation/components/Checkbox';
import { LABEL_PLACEMENT } from 'baseui/checkbox';
import { useMediaQuery } from 'tools/hooks/useMediaQuery';
import { TechemTheme } from '@techem/techem-theme';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import useHotKey from 'tools/hooks/useHotKey';

const Component: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const prevPath = paths.HeatingType;

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      // Assumption: previous page has to be filled with user input before the user is allowed to visit this page
      store.getState().onboardingInfo.heatingType === ''
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const [heatingSources, setHeatingSources] = useState<string[]>(
    store.getState().onboardingInfo.heatingSources
  );

  const isMobile = useMediaQuery('(max-width: 767px)');

  const selectionData = [
    {
      icon: option1Icon,
      key: CheckboxOptionKeys.HeatingSource.LongDistanceHeating,
      trackingId: 'checkbox_long-distance-heating'
    },
    {
      icon: option2Icon,
      key: CheckboxOptionKeys.HeatingSource.Gas,
      trackingId: 'checkbox_gas'
    },
    {
      icon: option3Icon,
      key: CheckboxOptionKeys.HeatingSource.HeatingOil,
      trackingId: 'checkbox_heating-oil'
    },
    {
      icon: option4Icon,
      key: CheckboxOptionKeys.HeatingSource.Pellets,
      trackingId: 'checkbox_pellets'
    },
    {
      icon: option5Icon,
      key: CheckboxOptionKeys.HeatingSource.Solar,
      trackingId: 'checkbox_solar'
    },
    {
      icon: option6Icon,
      key: CheckboxOptionKeys.HeatingSource.HeatPump,
      trackingId: 'checkbox_heat-pump'
    },
    {
      icon: option7Icon,
      key: CheckboxOptionKeys.HeatingSource.Other,
      trackingId: 'checkbox_other'
    }
  ].map((opt) => ({
    icon: opt.icon,
    label: t(translationKeyForCheckboxOption(opt.key)),
    optionKey: opt.key,
    trackingId: opt.trackingId
  }));

  const {
    control,
    register,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: { selectionData: selectionData }
  });
  const { fields } = useFieldArray({
    control,
    name: 'selectionData'
  });

  const validateSelection = () =>
    getValues('selectionData').length > 0 ? true : t('pageHeatingSourcesErrorNoneSelected');

  const isChecked = (item: any) => heatingSources.indexOf(item.optionKey) > -1;

  const handleCheckboxClick = (clickedItem: any) => {
    TrackerUtil.trackCheckboxClick(
      `${TrackerUtil.getPageName()}_${clickedItem.trackingId}`,
      clickedItem.trackingId
    );
    if (isChecked(clickedItem)) {
      // Remove clicked item.
      setHeatingSources(heatingSources.filter((source) => source !== clickedItem.optionKey));
    } else {
      // Add clicked item and sort new list.
      setHeatingSources(sortSelectedOptions([...heatingSources, clickedItem.optionKey]));
    }
  };

  const onSubmit = () => {
    store.dispatch(setHeatingSourcesInStore(heatingSources));

    TrackerUtil.trackNextBtnClick();
    navigate(paths.InputEMailAddress);
  };

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  useHotKey((event) => {
    if (event.key === '1') handleCheckboxClick(selectionData[0]);
    if (event.key === '2') handleCheckboxClick(selectionData[1]);
    if (event.key === '3') handleCheckboxClick(selectionData[2]);
    if (event.key === '4') handleCheckboxClick(selectionData[3]);
    if (event.key === '5') handleCheckboxClick(selectionData[4]);
    if (event.key === '6') handleCheckboxClick(selectionData[5]);
    if (event.key === '7') handleCheckboxClick(selectionData[6]);
  });

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={6} totalSteps={6} />
      </WizardStepsContainer>
      <TitleWithPrevPageLink
        onPrevClick={handlePrevBtnClicked}
        title={t('pageHeatingSourcesTitle')}
      />
      <PageDescription indented>{t('pageHeatingSourcesDescription')}</PageDescription>

      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Row className="justify-content-center mt-4 mt-lg-5">
          <Col xs={10} md={12} lg={10} className="text-center">
            <fieldset>
              <legend id="heating-sources-label" className="visually-hidden">
                {t('pageHeatingSourcesTitle')}
              </legend>
              <FormControlBaseWeb
                error={errors?.selectionData?.message}
                overrides={{
                  Caption: {
                    style: {
                      textAlign: 'center',
                      color: TechemTheme.colors.negative
                    }
                  }
                }}
              >
                <Row className="justify-content-center">
                  {fields.map((field, index) => {
                    return (
                      <Col xs={12} md key={field.optionKey} className="px-md-0">
                        <Row className={`${isMobile ? 'py-1' : ''}`}>
                          <Col
                            xs={12}
                            className={`my-md-3 px-md-0 ${isMobile ? 'd-block' : 'd-flex'}`}
                            style={{
                              backgroundColor: isMobile
                                ? TechemTheme.colors.backgroundSecondary
                                : 'transparent',
                              borderRadius: isMobile ? '4px' : '0px'
                            }}
                          >
                            <div
                              className="m-auto"
                              style={{
                                padding: isMobile ? '0 4px' : 'inherit'
                              }}
                            >
                              <Checkbox
                                checked={isChecked(field)}
                                {...register(`selectionData.${index}.optionKey` as const, {
                                  validate: validateSelection,
                                  onChange: () => handleCheckboxClick(field)
                                })}
                                labelPlacement={
                                  isMobile ? LABEL_PLACEMENT.right : LABEL_PLACEMENT.top
                                }
                                additionalTestId={field.optionKey}
                                topLevelStylingOverrides={
                                  isMobile
                                    ? {
                                        height: '60px',
                                        alignItems: 'center'
                                      }
                                    : {}
                                }
                                aria-labelledby="heating-sources-label"
                              >
                                <Col xs={12} className={`my-2 d-none d-md-block`}>
                                  <div className="d-flex w-100" style={{ height: '56px' }}>
                                    <Icon
                                      iconSrc={field.icon}
                                      handleClick={() => {}}
                                      centered
                                      size={
                                        field.optionKey === CheckboxOptionKeys.HeatingSource.Other
                                          ? ICONSIZE.large
                                          : ICONSIZE.xxlarge
                                      }
                                      alt={field.label}
                                      additionalTestId={field.optionKey}
                                    ></Icon>
                                  </div>
                                </Col>
                                <Col
                                  style={{
                                    minWidth: isMobile
                                      ? '0'
                                      : `${+getPixelSize(ICONSIZE.xxxlarge).split('px')[0] + 30}px`
                                  }}
                                  className="mb-0 mb-md-3 ms-2 ms-md-0"
                                >
                                  <span className="copy-s">{field.label}</span>
                                </Col>
                              </Checkbox>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    );
                  })}
                </Row>
              </FormControlBaseWeb>
            </fieldset>
          </Col>
        </Row>

        <Row className="justify-content-center py-5">
          <Col className="text-center col-auto">
            <Button
              size={SIZE.large}
              type="submit"
              disabled={heatingSources.length < 1}
              additionalTestId="next"
            >
              {t('nextBtnText')}
            </Button>
          </Col>
        </Row>
      </form>
    </Container>
  );
};

export default Component;
